import RouteBody from "../route/RouteBody";
import styles from './Employee.module.css'
import {useSelector, useDispatch} from "react-redux";
import {selectDrivesInRoutes} from "../route/routeSlice";
import _ from "lodash";
import classNames from "classnames";
import {courierIsLate} from "../utils/time";
import {Button} from "react-bootstrap";
import {assignRouteToHelper, removeRouteFromHelper} from "../route/routeApi";
import {loadRoutesAsync} from "../route/routeSlice";
import {loadEmployeesAsync} from "../../employee/employeeSlice";
import {useEffect, useState, useMemo} from "react";
import {useCity} from "../../../app/utils/use";
import {selectFiltersValueDate} from "../filter/filterSlice";
import {ROLE_CURTAIN_HELPER} from "../../../app/utils/const";
import { useError } from "../components/ErrorContext";
import CurtainCourierSelectModal from "../components/CurtainCourierSelectModal";
import {selectEmployees, updateEmployee} from "../../employee/employeeSlice";

const EmployeeBody = ({employee, isActive, mapRef}) => {

    const dispatch = useDispatch();
    const cityId = useCity();
    const date = useSelector(selectFiltersValueDate);
    const drivesInRoutes = useSelector(selectDrivesInRoutes);
    const employees = useSelector(selectEmployees);

    // Находим актуального курьера из обновленного списка по ID
    // Это важно для случая, когда employee prop приходит из отфильтрованного списка со старыми данными
    const actualEmployee = useMemo(() => {
        if (!employee?.id) return employee;
        // Ищем курьера в актуальном списке employees (baseEmployeesList) по ID и дате
        const found = _.find(employees, { id: employee.id });
        // Если нашли в актуальном списке, используем его данные, иначе используем prop employee
        return found || employee;
    }, [employee, employees, date]);

    const deriveAssignedCurtainCourierId = () => {
        // Используем actualEmployee вместо employee для получения актуальных данных
        return actualEmployee?.main_courier_id
            ?? actualEmployee?.helper_main_courier_id
            ?? actualEmployee?.assigned_main_courier_id
            ?? null;
    };

    const [isLoading, setIsLoading] = useState(false);
    const [showCourierSelectModal, setShowCourierSelectModal] = useState(false);
    const [assignedCurtainCourierId, setAssignedCurtainCourierId] = useState(deriveAssignedCurtainCourierId());
    const { showError } = useError();

    // Обновляем assignedCurtainCourierId при изменении main_courier_id или смене даты
    // Важно: main_courier_id теперь возвращается только для текущей даты
    // При смене даты employee должен обновляться с новым main_courier_id (или null)
    // Используем actualEmployee для получения актуальных данных из обновленного списка
    useEffect(() => {
        setAssignedCurtainCourierId(deriveAssignedCurtainCourierId());
    }, [
        date, // При смене даты обновляем значение
        actualEmployee?.id, // При смене курьера обновляем значение
        actualEmployee?.main_courier_id, // При изменении main_courier_id обновляем значение
        actualEmployee?.helper_main_courier_id,
        actualEmployee?.assigned_main_courier_id
    ]);
    
    // Проверка, является ли курьер "Помощником шторного"
    // Используем новое поле courier_role_id (3 = ROLE_CURTAIN_HELPER)
    // Поддерживаем обратную совместимость со старым полем schedule для отображения
    // Используем actualEmployee для получения актуальных данных
    const isHelperCurtainCourier = actualEmployee?.courier_role_id === ROLE_CURTAIN_HELPER ||
                                    (actualEmployee?.courier_role_id === undefined && 
                                     (actualEmployee?.schedule === 'Помощник шторного' || 
                                      actualEmployee?.tariff === 'Помощник шторного' || 
                                      actualEmployee?.tariffName === 'Помощник шторного'));

    const isHelperAssignedToCurtain = isHelperCurtainCourier && Boolean(assignedCurtainCourierId);
    const routeOwnerId = isHelperAssignedToCurtain ? assignedCurtainCourierId : actualEmployee?.id || employee.id;

    const filteredDrivesInRoutes = _.filter(drivesInRoutes, {courier: routeOwnerId})
    const lateDrives = _.filter(filteredDrivesInRoutes, (driveInRoute) => courierIsLate(driveInRoute))
    const completedDrives = _.filter(filteredDrivesInRoutes, (driveInRoute) => {
        return driveInRoute.drive.allowedChange === false;
    })
    
    // Проверка, есть ли маршрут у курьера
    const hasRoute = filteredDrivesInRoutes.length > 0;
    
    // Получаем ФИО главного шторного курьера для помощника
    const getMainCourierName = () => {
        if (!isHelperCurtainCourier || !assignedCurtainCourierId) {
            return null;
        }
        const mainCourier = _.find(employees, { id: assignedCurtainCourierId });
        return mainCourier ? mainCourier.name : null;
    };
    
    // Используем actualEmployee для отображения, но оставляем employee для API запросов (ID не меняется)
    const displayEmployee = actualEmployee || employee;
    
    const mainCourierName = getMainCourierName();
    const isRouteConfirmed = filteredDrivesInRoutes.length > 0 && filteredDrivesInRoutes[0]?.confirmed;
    
    // Проверка, есть ли выезды в статусе "В работе" или "Завершен"
    // Если есть, то маршрут нельзя удалить
    const hasActiveOrCompletedDrives = _.some(filteredDrivesInRoutes, (driveInRoute) => {
        const status = driveInRoute.drive?.status;
        // Проверяем различные варианты написания статусов
        return status === 'В работе' || 
               status === 'Завершен' || 
               status === 'Завершено' ||
               (status && status.toLowerCase().includes('работа')) ||
               (status && status.toLowerCase().includes('завершен'));
    });
    
    // Дополнительная проверка: если выезд не может быть изменен (allowedChange === false), 
    // значит он уже начат или завершен
    const hasNonEditableDrives = _.some(filteredDrivesInRoutes, (driveInRoute) => {
        return driveInRoute.drive?.allowedChange === false;
    });
    
    // Кнопку "Удалить маршрут" показываем только если:
    // 1. Помощник присвоен к шторному курьеру
    // 2. Нет активных или завершенных выездов (по статусу)
    // 3. Нет выездов, которые нельзя редактировать (начатые/завершенные)
    const canRemoveRoute = isHelperAssignedToCurtain && !hasActiveOrCompletedDrives && !hasNonEditableDrives;
    
    const handleAssignRoute = () => {
        // Дополнительная защита: проверяем роль перед отправкой запроса
        if (!isHelperCurtainCourier) {
            showError('Курьер не является помощником шторного', 'Ошибка при работе с маршрутом');
            return;
        }
        
        // Показываем модалку выбора шторного курьера
        setShowCourierSelectModal(true);
    };

    const handleCourierSelect = async (mainCourierId) => {
        if (!mainCourierId) {
            return;
        }
        setIsLoading(true);
        try {
            const response = await assignRouteToHelper({
                helperCourierId: employee.id,
                mainCourierId: mainCourierId,
                date: date
            });
            // Обработка ответа от бэкенда (может содержать main_courier_id, helper_courier_id, date, route_id)
            if (response.data && response.data.success !== false) {
                setAssignedCurtainCourierId(mainCourierId);
                // Обновляем employee в Redux, устанавливая main_courier_id
                // Это важно для немедленного обновления UI без ожидания WebSocket события
                dispatch(updateEmployee({
                    id: employee.id,
                    attributes: {
                        ...employee,
                        main_courier_id: mainCourierId
                    }
                }));
                // Перезагружаем список курьеров для обновления main_courier_id в UI
                dispatch(loadEmployeesAsync({
                    url: '/lsystem/api/v1/couriers',
                    params: {
                        cityId: cityId,
                        expand: 'workTime,regions,schedule',
                        date: date
                    }
                }));
                // Перезагружаем маршруты после присвоения
                // WebSocket также должен обновить данные автоматически
                dispatch(loadRoutesAsync({
                    cityId: cityId,
                    expand: 'drive',
                    date: date
                }));
            } else {
                // Обработка ошибки от бэкенда
                showError(response.data || { error: 'Неизвестная ошибка' }, 'Ошибка при работе с маршрутом');
            }
        } catch (error) {
            console.error('Ошибка при присвоении маршрута:', error);
            // Обработка ошибки сети или сервера
            const errorData = error.response?.data || {
                error: error.message || 'Ошибка при присвоении маршрута. Проверьте логи.'
            };
            showError(errorData, 'Ошибка при работе с маршрутом');
        } finally {
            setIsLoading(false);
        }
    };
    
    const handleRemoveRoute = async () => {
        // Дополнительная защита: проверяем роль перед отправкой запроса
        if (!isHelperCurtainCourier) {
            showError('Курьер не является помощником шторного', 'Ошибка при работе с маршрутом');
            return;
        }
        
        if (!isHelperAssignedToCurtain) {
            showError('У помощника нет присвоенного маршрута', 'Ошибка при работе с маршрутом');
            return;
        }
        
        // Проверка: если маршрут уже в работе или завершен, не позволяем удаление
        if (hasActiveOrCompletedDrives || hasNonEditableDrives) {
            showError('Невозможно удалить маршрут: есть выезды в работе или завершенные выезды', 'Ошибка при работе с маршрутом');
            return;
        }
        
        if (!window.confirm('Вы уверены, что хотите удалить маршрут у помощника?')) {
            return;
        }
        setIsLoading(true);
        try {
            const response = await removeRouteFromHelper({
                helperCourierId: employee.id,
                date: date
            });
            // Обработка ответа от бэкенда
            if (response.data && response.data.success !== false) {
                setAssignedCurtainCourierId(null);
                // Обновляем employee в Redux, устанавливая main_courier_id = null
                // Это важно для немедленного обновления UI без ожидания WebSocket события
                dispatch(updateEmployee({
                    id: employee.id,
                    attributes: {
                        ...employee,
                        main_courier_id: null,
                        helper_main_courier_id: null,
                        assigned_main_courier_id: null
                    }
                }));
                // Перезагружаем список курьеров для обновления main_courier_id в UI
                dispatch(loadEmployeesAsync({
                    url: '/lsystem/api/v1/couriers',
                    params: {
                        cityId: cityId,
                        expand: 'workTime,regions,schedule',
                        date: date
                    }
                }));
                // Перезагружаем маршруты после удаления
                // WebSocket также должен обновить данные автоматически
                dispatch(loadRoutesAsync({
                    cityId: cityId,
                    expand: 'drive',
                    date: date
                }));
            } else {
                // Обработка ошибки от бэкенда
                showError(response.data || { error: 'Неизвестная ошибка' }, 'Ошибка при работе с маршрутом');
            }
        } catch (error) {
            console.error('Ошибка при удалении маршрута:', error);
            // Обработка ошибки сети или сервера
            const errorData = error.response?.data || {
                error: error.message || 'Ошибка при удалении маршрута. Проверьте логи.'
            };
            showError(errorData, 'Ошибка при работе с маршрутом');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className={classNames({
            [styles.RouteShortInfo]: true
        })}>
            <div>
                <div className={styles.WorkTime}>
                    <strong>{displayEmployee.schedule}:</strong>&nbsp;
                    {displayEmployee.workTime?.time_from ?? 'не указано'} - {displayEmployee.workTime?.time_to ?? 'не указано'}
                </div>
                <div className={styles.CountDrives}>
                    <strong>Выезды:</strong>&nbsp;
                    {completedDrives.length} / {filteredDrivesInRoutes.length}
                </div>
                <div className="clearfix"/>
                <div className={styles.RouteConfirm}>
                    <div className={styles.RouteConfirmLeft}>
                        <strong>Маршрут:</strong>&nbsp;
                        {isHelperCurtainCourier && mainCourierName ? (
                            <span className={classNames({
                                [styles.RouteIsConfirmed]: isRouteConfirmed,
                                [styles.RouteIsNotConfirmed]: !isRouteConfirmed
                            })}>
                                {mainCourierName}
                            </span>
                        ) : (
                            <span className={classNames({
                                [styles.RouteIsConfirmed]: filteredDrivesInRoutes.length && filteredDrivesInRoutes[0].confirmed,
                                [styles.RouteIsNotConfirmed]: filteredDrivesInRoutes.length && !filteredDrivesInRoutes[0].confirmed
                            })}>
                                {!filteredDrivesInRoutes.length ? 'отсутствует' : filteredDrivesInRoutes[0]?.confirmed ? 'принят' : 'не принят'}
                            </span>
                        )}
                    </div>
                    <div className={styles.RouteConfirmRight}>
                        {(displayEmployee.is_part_time === true
                            || displayEmployee.is_part_time === 1
                            || displayEmployee.is_part_time === '1') && (
                            <span className={styles.PartTimeBadge}>Подработка</span>
                        )}
                        {isHelperCurtainCourier && !isHelperAssignedToCurtain && (
                            <Button 
                                variant="primary" 
                                size="sm" 
                                className={styles.AssignRouteBtn}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleAssignRoute();
                                }}
                                disabled={isLoading || !isHelperCurtainCourier}
                                title={!isHelperCurtainCourier ? 'Курьер не является помощником шторного' : ''}
                            >
                                Присвоить
                            </Button>
                        )}
                        {isHelperCurtainCourier && canRemoveRoute && (
                            <Button 
                                variant="danger" 
                                size="sm" 
                                className={styles.RemoveRouteBtn}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveRoute();
                                }}
                                disabled={isLoading || !isHelperCurtainCourier}
                                title={!isHelperCurtainCourier ? 'Курьер не является помощником шторного' : ''}
                            >
                                Удалить
                            </Button>
                        )}
                    </div>
                </div>
                <div className="clearfix"/>
                <div className={classNames({
                    [styles.LateDrivesWrap]: true,
                    [styles.HasLateDrives]: lateDrives.length > 0
                })}>
                    {filteredDrivesInRoutes.length ? lateDrives.length > 0 ? 'Опаздывает: ' + lateDrives.length : 'Не опаздывает' : null}
                </div>
            </div>
            {isActive ? <RouteBody mapRef={mapRef} employee={employee} routeOwnerId={routeOwnerId} isHelperCurtainCourier={isHelperCurtainCourier}/> : null}
            <CurtainCourierSelectModal
                show={showCourierSelectModal}
                helperCourierId={employee.id}
                date={date}
                onClose={() => setShowCourierSelectModal(false)}
                onSelect={handleCourierSelect}
            />
        </div>
    )
}

export default EmployeeBody;